import { createFileRoute } from "@tanstack/react-router";
import {
  TrendingUp, TrendingDown, Building2, Globe2, Users, Sparkles,
  DollarSign, BedDouble, UtensilsCrossed, Star, Bell, Search,
  ChevronRight, ArrowUpRight, MessageSquareHeart,
} from "lucide-react";

export const Route = createFileRoute("/executive")({
  head: () => ({
    meta: [
      { title: "Executive Dashboard — thatKorner Intelligence" },
      {
        name: "description",
        content:
          "A real-time executive command center for hotel brand CEOs — RevPAR, F&B performance, guest sentiment and AI concierge intelligence across the global portfolio.",
      },
      { property: "og:title", content: "Executive Dashboard — thatKorner Intelligence" },
      {
        property: "og:description",
        content:
          "Real-time RevPAR, F&B performance, guest sentiment and AI concierge intelligence across the global portfolio.",
      },
      { property: "og:url", content: "https://www.thatkorner.com/executive" },
    ],
    links: [{ rel: "canonical", href: "https://www.thatkorner.com/executive" }],
  }),
  component: ExecutiveDashboard,
});

/* ─────────────────────────── data ─────────────────────────── */

const KPIS = [
  { k: "Portfolio RevPAR", v: "$312.40", d: "+8.4%", up: true, icon: DollarSign, foot: "vs. last quarter" },
  { k: "Occupancy", v: "84.6%", d: "+3.2 pts", up: true, icon: BedDouble, foot: "62 properties" },
  { k: "F&B Revenue", v: "$48.2M", d: "+14.7%", up: true, icon: UtensilsCrossed, foot: "MTD · powered by Korner" },
  { k: "Guest NPS", v: "72", d: "+6", up: true, icon: Star, foot: "world-class tier" },
];

const REGIONS = [
  { r: "EMEA",         rev: "$184.2M", occ: 88, delta: "+9.1%", up: true,  hotels: 24 },
  { r: "Americas",     rev: "$162.8M", occ: 82, delta: "+5.4%", up: true,  hotels: 21 },
  { r: "Asia Pacific", rev: "$141.6M", occ: 86, delta: "+12.6%",up: true,  hotels: 14 },
  { r: "Middle East",  rev: "$ 72.4M", occ: 79, delta: "−1.8%", up: false, hotels: 3  },
];

const PROPERTIES = [
  { n: "The Korner — Paris",       loc: "Paris, FR",     revpar: 612, occ: 94, sent: 9.6 },
  { n: "Maison Korner — Tokyo",    loc: "Tokyo, JP",     revpar: 548, occ: 91, sent: 9.5 },
  { n: "The Korner — Dubai",       loc: "Dubai, AE",     revpar: 521, occ: 89, sent: 9.3 },
  { n: "Korner Residences — NYC",  loc: "New York, US",  revpar: 487, occ: 87, sent: 9.2 },
  { n: "Maison Korner — Milan",    loc: "Milan, IT",     revpar: 446, occ: 85, sent: 9.1 },
];

const REVENUE_BARS = [
  42, 58, 49, 71, 64, 84, 73, 95, 78, 88, 81, 96, 84, 92,
  101, 88, 96, 110, 102, 118, 109, 124, 116, 132,
];

const ALERTS = [
  { tone: "ember",   t: "Surge demand · Tokyo",        s: "+38% bookings vs. forecast — pricing engine adjusting." },
  { tone: "bone",    t: "F&B menu refresh · Paris",    s: "Korner concierge ready to roll out new tasting menu." },
  { tone: "ember",   t: "Sentiment dip · Doha",        s: "Spa wait-times trending down by 0.4 — review staffing." },
];

/* ─────────────────────────── page ─────────────────────────── */

function ExecutiveDashboard() {
  return (
    <section className="grain min-h-screen bg-ink px-4 pb-20 pt-24 text-bone md:px-8 md:pt-28">
      <div className="mx-auto max-w-[1480px]">
        {/* Top bar */}
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-bone/10 pb-6">
          <div>
            <p className="font-display text-[11px] uppercase tracking-[0.32em] text-ember">◆ Executive Command Center</p>
            <h1 className="h-display mt-3 text-[clamp(2rem,4.4vw,3.4rem)] leading-[0.95] text-bone">
              Good evening, <span className="font-script italic text-ember">Madame Laurent.</span>
            </h1>
            <p className="mt-2 text-sm text-bone/60">
              62 properties · 11 countries · live across the global portfolio · Tuesday, May 12 · 21:04 CET
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 rounded-full border border-bone/15 bg-bone/[0.03] px-3.5 py-2 md:flex">
              <Search className="h-4 w-4 text-bone/70" />
              <input
                placeholder="Ask Kori anything…"
                className="w-56 bg-transparent text-sm text-bone placeholder:text-bone/70 focus:outline-none"
              />
              <kbd className="rounded border border-bone/15 px-1.5 py-0.5 text-[10px] text-bone/70">⌘K</kbd>
            </div>
            <button className="relative flex h-10 w-10 items-center justify-center rounded-full border border-bone/15 bg-bone/[0.03] hover:border-ember/40">
              <Bell className="h-4 w-4 text-bone/70" />
              <span className="absolute right-2 top-2 h-1.5 w-1.5 animate-pulse rounded-full bg-ember" />
            </button>
            <div className="flex h-10 items-center gap-2.5 rounded-full border border-bone/15 bg-bone/[0.03] pl-1 pr-4">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-ember to-ember/60 font-display text-xs font-bold text-ink">EL</span>
              <span className="hidden text-xs text-bone/80 sm:inline">Élise Laurent · CEO</span>
            </div>
          </div>
        </header>

        {/* KPI strip */}
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {KPIS.map(({ k, v, d, up, icon: Icon, foot }) => (
            <div
              key={k}
              className="group relative overflow-hidden rounded-2xl border border-bone/10 bg-gradient-to-br from-bone/[0.06] via-bone/[0.02] to-transparent p-5 transition-all hover:border-ember/30 hover:shadow-[0_20px_60px_-30px_rgba(229,90,43,0.6)]"
            >
              <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-ember/10 blur-3xl opacity-0 transition-opacity group-hover:opacity-100" />
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ember/15 ring-1 ring-ember/30">
                  <Icon className="h-4 w-4 text-ember" />
                </div>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] tabular-nums ${
                    up ? "bg-ember/15 text-ember" : "bg-bone/10 text-bone/70"
                  }`}
                >
                  {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {d}
                </span>
              </div>
              <p className="font-display mt-5 text-3xl font-semibold text-bone md:text-4xl">{v}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-bone/70">{k}</p>
              <p className="mt-3 text-[11px] text-bone/70">{foot}</p>
            </div>
          ))}
        </div>

        {/* Main grid */}
        <div className="mt-4 grid grid-cols-12 gap-4">
          {/* Revenue chart */}
          <div className="col-span-12 overflow-hidden rounded-2xl border border-bone/10 bg-gradient-to-br from-bone/[0.05] to-transparent p-6 lg:col-span-8">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-bone/70">Group revenue · last 24 days</p>
                <div className="mt-2 flex items-baseline gap-3">
                  <p className="font-display text-3xl font-semibold text-bone md:text-4xl">$561.0M</p>
                  <span className="inline-flex items-center gap-1 text-sm text-ember">
                    <ArrowUpRight className="h-4 w-4" /> +11.4% YoY
                  </span>
                </div>
              </div>
              <div className="flex gap-1.5 rounded-full border border-bone/10 bg-bone/[0.03] p-1 text-[11px]">
                {["7D", "24D", "QTD", "YTD"].map((t, i) => (
                  <button
                    key={t}
                    className={`rounded-full px-3 py-1 transition ${
                      i === 1 ? "bg-ember text-ink" : "text-bone/60 hover:text-bone"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* SVG line + bars */}
            <div className="relative mt-6 h-56">
              {/* gridlines */}
              <div className="absolute inset-0 flex flex-col justify-between">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-px w-full bg-bone/[0.06]" />
                ))}
              </div>
              {/* bars */}
              <div className="absolute inset-x-0 bottom-0 flex h-full items-end gap-[6px]">
                {REVENUE_BARS.map((h, i) => (
                  <div key={i} className="group relative flex-1">
                    <div
                      className="w-full rounded-t-md bg-gradient-to-t from-ember/20 via-ember/55 to-ember/95 transition-all hover:to-bone"
                      style={{ height: `${h}%` }}
                    />
                  </div>
                ))}
              </div>
              {/* sparkline overlay */}
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
                <defs>
                  <linearGradient id="ln" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="oklch(0.96 0.02 80)" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="oklch(0.96 0.02 80)" stopOpacity="0.1" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,72 L8,60 L16,64 L24,48 L32,52 L40,38 L48,44 L56,28 L64,36 L72,24 L80,30 L88,18 L96,22 L100,12"
                  fill="none"
                  stroke="oklch(0.96 0.02 80)"
                  strokeWidth="0.6"
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="mt-3 flex justify-between text-[10px] text-bone/70">
              <span>Apr 19</span><span>Apr 24</span><span>Apr 29</span><span>May 4</span><span>May 9</span><span>Today</span>
            </div>
          </div>

          {/* Sentiment + AI insight */}
          <div className="col-span-12 flex flex-col gap-4 lg:col-span-4">
            {/* Sentiment radial */}
            <div className="relative overflow-hidden rounded-2xl border border-bone/10 bg-gradient-to-br from-ember/15 via-ember/[0.06] to-transparent p-6">
              <div className="absolute -right-10 -top-12 h-40 w-40 rounded-full bg-ember/25 blur-3xl" />
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.22em] text-bone/65">Guest sentiment · live</p>
                <MessageSquareHeart className="h-4 w-4 text-ember" />
              </div>
              <div className="mt-4 flex items-center gap-5">
                <div className="relative h-24 w-24">
                  <svg viewBox="0 0 36 36" className="h-24 w-24 -rotate-90">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="oklch(1 0 0 / 0.08)" strokeWidth="3" />
                    <circle
                      cx="18" cy="18" r="15.9" fill="none"
                      stroke="oklch(0.7 0.18 35)" strokeWidth="3" strokeLinecap="round"
                      strokeDasharray="92, 100"
                    />
                  </svg>
                  <div className="absolute inset-0 grid place-items-center">
                    <span className="font-display text-2xl font-semibold text-bone">9.2</span>
                  </div>
                </div>
                <div className="space-y-1.5 text-sm">
                  <p className="text-bone/85">14,628 guest signals today</p>
                  <p className="flex items-center gap-1.5 text-ember">
                    <TrendingUp className="h-3.5 w-3.5" /> +0.3 vs. last week
                  </p>
                  <p className="text-[11px] text-bone/70">Top theme: <span className="text-bone/80">"warmth of staff"</span></p>
                </div>
              </div>
            </div>

            {/* AI insight card */}
            <div className="relative overflow-hidden rounded-2xl border border-ember/30 bg-ember p-6 text-ink">
              <div className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-ink/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em]">
                <Sparkles className="h-3 w-3" /> Kori · AI brief
              </div>
              <p className="font-display text-[10px] uppercase tracking-[0.3em] text-ink/70">Tonight's recommendation</p>
              <p className="font-display mt-3 text-lg font-semibold leading-snug">
                Shift 12% of marketing spend from EMEA leisure to APAC business — projected
                <span className="font-script italic"> +$1.84M </span>
                Q3 contribution.
              </p>
              <button className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-display text-[11px] font-bold uppercase tracking-[0.2em] text-bone hover:-translate-y-0.5 transition-transform">
                Open full brief <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>

          {/* Regions */}
          <div className="col-span-12 rounded-2xl border border-bone/10 bg-gradient-to-br from-bone/[0.05] to-transparent p-6 lg:col-span-7">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe2 className="h-4 w-4 text-ember" />
                <p className="text-[10px] uppercase tracking-[0.22em] text-bone/70">Performance by region</p>
              </div>
              <span className="text-[11px] text-bone/70">Quarter to date</span>
            </div>

            <div className="mt-5 space-y-4">
              {REGIONS.map((r) => (
                <div key={r.r} className="grid grid-cols-12 items-center gap-3">
                  <div className="col-span-3">
                    <p className="font-display text-sm text-bone">{r.r}</p>
                    <p className="text-[11px] text-bone/70">{r.hotels} hotels</p>
                  </div>
                  <div className="col-span-6">
                    <div className="relative h-2 overflow-hidden rounded-full bg-bone/[0.06]">
                      <div
                        className={`h-full rounded-full ${
                          r.up
                            ? "bg-gradient-to-r from-ember/60 to-ember"
                            : "bg-gradient-to-r from-bone/30 to-bone/50"
                        }`}
                        style={{ width: `${r.occ}%` }}
                      />
                    </div>
                    <p className="mt-1 text-[10px] text-bone/70">Occupancy {r.occ}%</p>
                  </div>
                  <p className="col-span-2 text-right font-display tabular-nums text-bone">{r.rev}</p>
                  <p className={`col-span-1 text-right text-xs tabular-nums ${r.up ? "text-ember" : "text-bone/70"}`}>
                    {r.delta}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Alerts */}
          <div className="col-span-12 rounded-2xl border border-bone/10 bg-gradient-to-br from-bone/[0.05] to-transparent p-6 lg:col-span-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-ember" />
                <p className="text-[10px] uppercase tracking-[0.22em] text-bone/70">Live signals</p>
              </div>
              <span className="text-[11px] text-bone/70">Past 60 min</span>
            </div>

            <ul className="mt-5 space-y-3">
              {ALERTS.map((a) => (
                <li
                  key={a.t}
                  className="group flex items-start gap-3 rounded-xl border border-bone/10 bg-bone/[0.02] p-3.5 transition-colors hover:border-ember/30"
                >
                  <span
                    className={`mt-1 inline-block h-2 w-2 flex-none rounded-full ${
                      a.tone === "ember" ? "bg-ember shadow-[0_0_12px_rgba(229,90,43,0.7)]" : "bg-bone/40"
                    }`}
                  />
                  <div className="min-w-0">
                    <p className="font-display text-sm text-bone">{a.t}</p>
                    <p className="mt-0.5 truncate text-[12px] text-bone/70">{a.s}</p>
                  </div>
                  <ChevronRight className="ml-auto h-4 w-4 flex-none text-bone/70 transition-transform group-hover:translate-x-0.5 group-hover:text-ember" />
                </li>
              ))}
            </ul>
          </div>

          {/* Top properties */}
          <div className="col-span-12 rounded-2xl border border-bone/10 bg-gradient-to-br from-bone/[0.05] to-transparent p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-ember" />
                <p className="text-[10px] uppercase tracking-[0.22em] text-bone/70">Top performing properties</p>
              </div>
              <button className="text-[11px] text-ember hover:underline">View all 62 →</button>
            </div>

            <div className="mt-5 overflow-x-auto">
              <table className="w-full min-w-[640px] border-separate border-spacing-y-1 text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-bone/70">
                    <th className="px-3 py-2 font-normal">Property</th>
                    <th className="px-3 py-2 font-normal">Location</th>
                    <th className="px-3 py-2 text-right font-normal">RevPAR</th>
                    <th className="px-3 py-2 text-right font-normal">Occupancy</th>
                    <th className="px-3 py-2 text-right font-normal">Sentiment</th>
                  </tr>
                </thead>
                <tbody>
                  {PROPERTIES.map((p) => (
                    <tr key={p.n} className="bg-bone/[0.02] transition-colors hover:bg-bone/[0.05]">
                      <td className="rounded-l-xl px-3 py-3">
                        <div className="flex items-center gap-3">
                          <span className="grid h-8 w-8 place-items-center rounded-lg bg-ember/15 text-ember">
                            <Building2 className="h-3.5 w-3.5" />
                          </span>
                          <span className="font-display text-bone">{p.n}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-bone/60">{p.loc}</td>
                      <td className="px-3 py-3 text-right font-display tabular-nums text-bone">${p.revpar}</td>
                      <td className="px-3 py-3 text-right">
                        <div className="ml-auto flex w-32 items-center gap-2">
                          <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-bone/[0.08]">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-ember/60 to-ember"
                              style={{ width: `${p.occ}%` }}
                            />
                          </div>
                          <span className="w-8 text-right text-[11px] tabular-nums text-bone/70">{p.occ}%</span>
                        </div>
                      </td>
                      <td className="rounded-r-xl px-3 py-3 text-right">
                        <span className="inline-flex items-center gap-1 rounded-full bg-ember/15 px-2 py-0.5 text-[11px] text-ember">
                          <Star className="h-3 w-3 fill-ember" />
                          {p.sent}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer strip */}
          <div className="col-span-12 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-bone/10 bg-bone/[0.02] px-5 py-4 text-[12px] text-bone/70">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-ember" />
              <span>4,128 staff online · 31,402 in-house guests</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ember opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-ember" />
              </span>
              <span>All systems nominal · synced 4 sec ago</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
